import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { Link, useLocation } from "react-router-dom";

import { isBoardOperator } from "../../../../types/Board";

const NavRoot = styled.nav`
  width: 100%;
  height: 48px;
  border-top: 1px solid var(--color-border-subtle);
  padding: 0 var(--space-4);
  display: flex;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border-strong);
    border-radius: var(--radius-pill);
  }
`;

const NavList = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 100%;
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-3);
  height: 32px;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    color: var(--color-text);
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  .MuiSvgIcon-root {
    font-size: 16px;
    color: inherit;
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-accent-subtle);
      color: var(--color-accent-text);
      font-weight: var(--font-weight-semibold);

      &:hover {
        background: var(--color-accent-subtle);
        color: var(--color-accent-text);
      }
    `}
`;

type Props = {
  allowedRoutes: any;
  user?: { communityId?: string } | null;
};

const NavigationMenu: React.FC<Props> = ({ allowedRoutes, user }) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Tribed-team-only entries (Brain) are gated on the admin's home community,
  // the same rule the issue board uses. Every coach is role ADMIN(101) of their
  // own community, so role cannot tell operators and coaches apart.
  const operator = isBoardOperator({ communityId: user?.communityId });

  return (
    <NavRoot>
      <NavList>
        {allowedRoutes
          .filter((item) => !item.hidden && (!item.operatorOnly || operator))
          .map((item) => {
            const { Icon, title, route } = item;
            const isActive =
              currentPath === route ||
              (route !== "/" && currentPath.startsWith(route + "/"));

            return (
              <NavLink key={route} to={route} $active={isActive}>
                {Icon && <Icon />}
                <span>{title}</span>
              </NavLink>
            );
          })}
      </NavList>
    </NavRoot>
  );
};

const mapStateToProps = (state) => ({
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(NavigationMenu);
